// Cihaz Ölçümleri sekmesinin görsel parçaları: ölçüm türü kartı (son değer +
// durum + mini eğilim), durum rozeti ve detay kartındaki özet satırı.

import React from "react";
import { Box, ButtonBase, Skeleton, Typography, alpha } from "@mui/material";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { STATUS_META } from "./measurementRanges";

/** "Normal / Yüksek / Düşük" rozeti — renkli nokta + metin (kimlik yalnız renge bırakılmaz). */
export function StatusPill({ status, size = "small" }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: size === "small" ? 0.9 : 1.1,
        py: 0.2,
        borderRadius: 999,
        bgcolor: meta.bg,
        color: meta.color,
        fontSize: size === "small" ? "0.68rem" : TYPO.label,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: meta.color }} />
      {meta.label}
    </Box>
  );
}

/** Kartın altındaki mini eğilim çizgisi (son ölçümler, eksensiz). */
function Sparkline({ points, color }) {
  const vals = points.filter((v) => v != null);
  if (vals.length < 2) return <Box sx={{ height: 32 }} />;
  const w = 100;
  const h = 32;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const xy = vals.map((v, i) => [
    (i / (vals.length - 1)) * w,
    h - 3 - ((v - min) / span) * (h - 6),
  ]);
  const d = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const [lx, ly] = xy[xy.length - 1];
  return (
    <Box component="svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" sx={{ width: "100%", height: h, display: "block", overflow: "visible" }} aria-hidden>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={alpha(color, 0.08)} stroke="none" />
      <path d={d} fill="none" stroke={color} strokeWidth={1.75} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r={2.5} fill={color} vectorEffect="non-scaling-stroke" />
    </Box>
  );
}

/**
 * Ölçüm türü kartı. Seçili kart tema rengi kenarlık + açık zemin.
 *
 * @param {React.ElementType} icon
 * @param {string} label - "Tansiyon"
 * @param {string} value - "132/85"
 * @param {string} unit - "mmHg"
 * @param {string|null} status - "normal" | "high" | "low" | null
 * @param {string} meta - "2 gün önce · 24 ölçüm"
 * @param {number[]} trend - mini çizgi için son değerler (eskiden yeniye)
 */
export function MetricCard({ icon: Icon, label, value, unit, status, meta, trend = [], active, onClick, palette }) {
  const tone = palette?.primary || COLORS.slate700;
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={active}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        gap: 1,
        p: 2,
        borderRadius: 3,
        bgcolor: active ? alpha(tone, 0.04) : COLORS.white,
        border: `1px solid ${active ? tone : COLORS.slate200}`,
        boxShadow: active ? `0 0 0 3px ${alpha(tone, 0.12)}` : "0 1px 2px rgba(15,23,42,0.04)",
        transition: "border-color .15s, box-shadow .15s, background-color .15s",
        "&:hover": { borderColor: active ? tone : COLORS.slate300 },
        "&:focus-visible": { outline: `2px solid ${alpha(tone, 0.5)}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 2,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(tone, 0.1),
            color: tone,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 16 }} /> : null}
        </Box>
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: TYPO.label, fontWeight: 600, color: COLORS.slate600 }}>
          {label}
        </Typography>
        <StatusPill status={status} />
      </Box>
      <Typography sx={{ lineHeight: 1.1, fontVariantNumeric: "tabular-nums", color: COLORS.slate800 }}>
        <Box component="span" sx={{ fontSize: "1.625rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
          {value}
        </Box>
        {unit ? (
          <Box component="span" sx={{ fontSize: TYPO.label, fontWeight: 500, color: COLORS.slate500, ml: 0.5 }}>
            {unit}
          </Box>
        ) : null}
      </Typography>
      <Sparkline points={trend} color={tone} />
      <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
        {meta}
      </Typography>
    </ButtonBase>
  );
}

export function MetricCardSkeleton() {
  return (
    <Box sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.slate200}`, bgcolor: COLORS.white, display: "grid", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 2 }} />
        <Skeleton width="45%" />
      </Box>
      <Skeleton width="55%" height={36} />
      <Skeleton variant="rounded" height={32} />
      <Skeleton width="60%" />
    </Box>
  );
}

/** Detay kartının sağ sütunundaki etiket–değer satırı. */
export function StatRow({ label, value, hint, children, last = false }) {
  return (
    <Box
      sx={{
        py: 1.25,
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1.5,
        borderBottom: last ? "none" : `1px solid ${COLORS.slate100}`,
      }}
    >
      <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>{label}</Typography>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {children || (
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
            {value}
          </Typography>
        )}
        {hint ? <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>{hint}</Typography> : null}
      </Box>
    </Box>
  );
}
